import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';
import { completePasswordSetup } from '../services/apiUserProfile.ts';

export default function PasswordSetup() {
  const { isDark } = useTheme();
  const [token, setToken] = useState<string | null>(null);
  const captured = useRef(false);
  const submitting = useRef(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [error, setError] = useState('');
  useLayoutEffect(() => {
    if (captured.current) return;
    captured.current = true;
    const value = new URLSearchParams(window.location.hash.slice(1)).get('token');
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
    if (value && /^[A-Za-z0-9_-]{43}$/.test(value)) setToken(value);
    else setInvalid(true);
  }, []);
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!token || submitting.current) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    const password = String(data.get('password') || '');
    const confirmation = String(data.get('confirmation') || '');
    if (password.length < 12 || new Blob([password]).size > 72) {
      setError('Use pelo menos 12 caracteres. Se a senha for muito longa, reduza seu tamanho.'); return;
    }
    if (password !== confirmation) { setError('As senhas precisam ser iguais. Confira e tente novamente.'); return; }
    submitting.current = true; setBusy(true); setError('');
    try {
      await completePasswordSetup({ token, new_password: password, confirm_password: confirmation });
      form.reset(); setToken(null); setDone(true);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if ([400, 401, 403, 404, 410].includes(status || 0)) { setInvalid(true); setToken(null); }
      else if (status === 422) setError('Confira a senha e a confirmação e tente novamente.');
      else if (status === 429) setError('Muitas tentativas. Aguarde alguns minutos antes de tentar novamente.');
      else setError('Não foi possível definir sua senha. Tente novamente em alguns instantes.');
    } finally { submitting.current = false; setBusy(false); }
  }
  const inputClass = `mt-1 min-h-[44px] w-full rounded-xl border p-3 outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-50 ${isDark ? 'border-gray-600 bg-gray-700' : 'border-slate-300 bg-white'}`;
  return <main className={`flex min-h-[100dvh] items-center justify-center p-4 ${isDark ? 'bg-gray-900 text-gray-100' : 'bg-sminy-canvas text-slate-900'}`}>
    <section className={`w-full max-w-md space-y-5 rounded-xl border p-6 ${isDark ? 'border-gray-700 bg-gray-800' : 'border-slate-200 bg-white'}`}>
      {done ? <CheckCircle2 aria-hidden="true" className="text-sminy-primary" /> : <KeyRound aria-hidden="true" className="text-sminy-primary" />}
      <h1 className="text-lg font-semibold">{done ? 'Senha definida com sucesso' : 'Crie sua senha'}</h1>
      {done ? <p role="status" className="text-sm">Sua senha foi salva. Entre com seu email e a nova senha para acessar sua conta.</p> : invalid ? <p role="alert" className="text-sm">Este link está ausente, é inválido, expirou ou já foi utilizado. Peça ao administrador da clínica para reenviar o link para definir sua senha.</p> : <form onSubmit={submit} className="space-y-4">
        <p className="text-sm">Use pelo menos 12 caracteres para proteger sua conta.</p>
        <label className="block text-sm">Nova senha<input className={inputClass} name="password" type="password" autoComplete="new-password" minLength={12} maxLength={72} required disabled={busy} /></label>
        <label className="block text-sm">Confirmar nova senha<input className={inputClass} name="confirmation" type="password" autoComplete="new-password" minLength={12} maxLength={72} required disabled={busy} /></label>
        {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
        <button type="submit" disabled={busy || !token} className="min-h-[44px] w-full rounded-xl bg-sminy-primary px-4 py-3 text-sm text-white hover:bg-sminy-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 disabled:opacity-50">{busy ? 'Salvando...' : 'Definir senha'}</button>
      </form>}
      <Link to="/" className="inline-flex min-h-[44px] items-center text-sm text-sminy-primary underline focus-visible:ring-2 focus-visible:ring-sminy-primary">Entrar na minha conta</Link>
    </section>
  </main>;
}
